export enum AppView {
  DASHBOARD = 'DASHBOARD', // Készlet lista
  DAILY_OPS = 'DAILY_OPS', // Napi Munkák (Kassza, Selejt...)
  INVENTORY_SCAN = 'INVENTORY_SCAN', // Leltározás
  IMPORT_DATA = 'IMPORT_DATA', // Adatbázis
  EMAIL_GENERATOR = 'EMAIL_GENERATOR',
  RECONCILIATION = 'RECONCILIATION', // Napló
  SETTINGS = 'SETTINGS',
  PROFILE = 'PROFILE', // Profil & Biztonság
  LABEL_PRINTER = 'LABEL_PRINTER', // Címkék nyomtatása
  USER_MANAGEMENT = 'USER_MANAGEMENT', // Munkatársak és Jogosultságok
  POS_HISTORY = 'POS_HISTORY', // Kosár előzmények
  SUSPENDED_CARTS = 'SUSPENDED_CARTS', // Parkolt kosarak
  BOSS_DASHBOARD = 'BOSS_DASHBOARD', // Főnök Dashboard
}

export interface SupplierProduct {
  ean: string;
  name: string;
  sku: string;
  netPrice: number; // Net purchase price (2 decimal places)
  sellingPrice: number; // Gross selling price (integer, rounded)
  netSellingPrice?: number; // Net selling price (2 decimal places)
  vatRate?: number; // VAT percentage: 0, 5, 18, or 27 (default: 27)
  isWeighable?: boolean;
  unitWeight?: number;
  unit?: 'db' | 'm' | 'kg';
  minQuantity?: number | null; // Alert limit - NULL = no limit, 0 = valid limit
}

export interface InventoryItem {
  ean: string;
  quantity: number;
  scannedAt: Date;
  min_quantity?: number;
  on_shopping_list?: boolean;
  isWeighable?: boolean;
  unitWeight?: number;
  unit?: 'db' | 'm' | 'kg';
  netPrice?: number; // Net purchase price (2 decimal places)
  netSellingPrice?: number; // Net selling price (2 decimal places)
  vatRate?: number; // VAT percentage: 0, 5, 18, or 27 (default: 27)
}

export interface MergedItem {
  ean: string;
  name: string;
  sku: string;
  netPrice: number;
  sellingPrice: number;
  countedQuantity: number;
  totalValue: number;
  status: 'MATCHED' | 'UNKNOWN' | 'MISSING';
}

export type InventoryMap = Record<string, InventoryItem>;
export type SupplierMap = Record<string, SupplierProduct>;

export interface Colleague {
  id: string;
  full_name: string;
  email: string;
  role: 'admin' | 'employee';
  is_active: boolean;
  access_labels?: boolean;
  access_email?: boolean;
  access_import?: boolean;
  access_pos?: boolean;
  barcode_id?: string;
  created_at: string;
  updated_at: string;
}

export interface Permissions {
  hasImportModule: boolean;
  hasLabelModule: boolean;
  hasEmailModule: boolean;
  hasProcurementModule: boolean;
  hasPosModule: boolean;
  hasBossDashboard?: boolean;
}

export type ScanMode = 'COUNT' | 'SCRAP' | 'RETURN' | 'INTERNAL' | 'SALE';

export type FilterMode = 'ALL' | 'LOW_STOCK' | 'SHOPPING_LIST';

export interface ScannerState {
  barcodeInput: string;
  lastScanned: string | null;
  newName: string;
  newPrice: string; // Legacy: kept for backward compatibility (treated as Net purchase)
  newSellingPrice: string; // Legacy: kept for backward compatibility (treated as Gross selling)
  // New hybrid pricing fields
  newPurchaseNet?: number;
  newPurchaseGross?: number;
  newSellingNet?: number;
  newSellingGross?: number;
  newVatRate?: number;
  newMinQuantity: string;
  newIsWeighable: boolean;
  newUnitWeight: string;
  newUnit?: 'db' | 'm' | 'kg';
  scanMode: ScanMode;
  transactionQty: number;
  // POS/Cart state
  cart: Array<{ ean: string; name: string; price: number; quantity: number }>;
  isCheckoutModalOpen: boolean;
  checkoutStep: 'SELECT_TYPE' | 'CONFIRM';
  selectedPaymentType: 'CASH' | 'INVOICE' | 'CARD' | 'OTHER';
  discountPercent: number;
  pendingAdjustment: any | null;
}

// Service-related types
export interface LogItem {
  id: string;
  action_type: string;
  details: string;
  created_at: string;
  user_name?: string; // User name who performed the action (for company-wide logs)
}

export interface TransactionItemInput {
  ean: string;
  quantity: number; // DELTA value (e.g., 1, -1, 5, etc.)
  price?: number; // GROSS purchase price (for backward compatibility)
  selling_price?: number; // GROSS selling price (for backward compatibility)
  net_price?: number; // NET purchase price (explicit, prevents price shrinking)
  net_selling_price?: number; // NET selling price (explicit)
  vat_rate?: number; // VAT percentage (explicit)
  name?: string;
  unit_weight?: number;
  is_weighable?: boolean;
  unit?: string;
}

export interface TransactionResult {
  transaction_id: string;
  ean: string;
  new_quantity: number;
  updated_at: string;
  low_stock?: boolean;
  min_quantity?: number;
  product_name?: string;
  on_shopping_list?: boolean;
}

export interface Company {
  id: string;
  name: string;
  logo_url: string | null;
  ui_settings: {
    primary_color?: string;
    secondary_color?: string;
    accent_color?: string;
    // Régi mezők (backward compatibility)
    background_color?: string;
    card_background?: string;
    // Új dark mode mezők
    background_color_light?: string;
    background_color_dark?: string;
    card_background_light?: string;
    card_background_dark?: string;
    // Login testreszabás
    login_subtitle?: string;
  };
}
